import { SkeletonWooDark } from '@/components/skeleton/skeleton.woo-dark';
import { useGetPersonalInfo } from '@/hooks/api';
import { WooClose, WooMenu, WooIdWithText } from '@nosinovacao/fe-icons';
import {
  SheetClosePrimitive,
  SheetContentPrimitive,
  SheetPrimitive,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { type ComponentType } from 'react';
import { useNavbarContext, type NavSheetContentSections } from '../../context/navbar';
import { MainNavContentWooDark } from './main-section';
import { TvNavSheetContentWooDark } from './tv-section';

const navSheetContent: Record<
  NavSheetContentSections,
  ComponentType<{
    onClose: () => void;
    goToSection: (section: Exclude<NavSheetContentSections, 'main'>) => void;
    goToMainSection: () => void;
    currentSection: NavSheetContentSections;
  }>
> = {
  main: MainNavContentWooDark,
  tv: TvNavSheetContentWooDark,
  services: MainNavContentWooDark,
} as const;

export function MobileNavbarWooDark() {
  const { t } = useI18n();
  const {
    sheetOpen,
    setSheetOpen,
    activeSection: sectionName,
    setActiveSection: setSectionName,
  } = useNavbarContext();

  const NavSheetContent = navSheetContent[sectionName];

  return (
    <SheetPrimitive open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetContentPrimitive
        skin="dark"
        aria-label={t('Common.Navbar.ContentLabel')}
        className="fixed inset-y-0 right-0 z-50 w-dvw bg-nos-neutral-900 py-6 tablet:w-[min(50dvw,384px)] [&>*]:flex [&>*]:flex-col"
      >
        <div className="size-full">
          <div className="flex items-center justify-between gap-5 px-4 pb-6">
            <WooIdWithText className="text-nos-neutral-0" />
            <SheetClosePrimitive aria-label={t('Common.Navbar.Close')} data-tid="button-close-menu">
              <WooClose width={32} height={32} className="text-nos-neutral-0" />
            </SheetClosePrimitive>
          </div>
          <NavSheetContent
            onClose={() => setSheetOpen(false)}
            goToSection={setSectionName}
            goToMainSection={() => setSectionName('main')}
            currentSection={sectionName}
          />
        </div>
      </SheetContentPrimitive>
    </SheetPrimitive>
  );
}

export function MobileNavbarTriggerWooDark() {
  const { isLoading } = useGetPersonalInfo();
  const { setSheetOpen } = useNavbarContext();
  return (
    <SkeletonWooDark
      loading={isLoading}
      className="bg-transparent"
      loaderChildren={<WooMenu width={32} height={32} />}
    >
      <button
        type="button"
        className="rounded-md text-nos-neutral-1000 outline-none focus-visible:outline-woo-blue"
        data-tid="button-open-menu"
        onClick={() => {
          setSheetOpen(true);
        }}
      >
        <WooMenu width={32} height={32} />
      </button>
    </SkeletonWooDark>
  );
}
